import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormCard } from "@/components/Core/FormCard";
import CompetenceRow from "@/components/Shared/CompetenceTab/CompetenceRow";
import CompetencesController from "../../../components/Shared/Competences/CompetencesController";
import SupplementValueModal from "../../../components/Shared/SupplementValueModal/SupplementValueModal";
import Competence from "../../../models/Competence";
import Consultant, { UpdateConsultantRequest } from "../../../models/Consultant";
import { useCollectiveAgreement } from "../../../networking/collective-agreement.hooks";
import { useOrganisation } from "../../../networking/organisation.hooks";
import {
	addNewCompetence,
	getCompetencesLabels,
	initCompetence,
	removeCompetence,
} from "../../../utils/competence.utils";
import { toast } from "../../../utils/toast";

type CompetenceInputs = {
	competence: Competence;
};

interface CompetenceTabProps {
	user: Consultant;
	onUpdateUser: (user: Consultant, update: UpdateConsultantRequest) => any;
}

const CompetenceTab: React.FC<CompetenceTabProps> = ({ user, onUpdateUser }) => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();

	const [competences, setCompetences] = useState<Competence[]>([]);
	const [supplementValueModal, setSupplementValueModal] = useState<boolean>(false);
	const [supplementCompetence, setSupplementCompetence] = useState<Competence>();
	const [resetCompetences, setResetCompetences] = useState<boolean>(false);

	// Get holiday pay from collective agreement
	const collectiveAgreementId = user?.profile?.employment?.colAgreement?.id;
	const { collectiveAgreement } = useCollectiveAgreement(
		collectiveAgreementId,
		!!collectiveAgreementId
	);
	const holidayPay = collectiveAgreement?.vacationSalaryPercent || 0;

	const { control, handleSubmit } = useForm<CompetenceInputs>({
		defaultValues: { competence: {} },
	});

	const handleUpdateUser = useCallback(
		(newCompetences: Competence[]) => {
			if (user?.id) {
				onUpdateUser &&
					onUpdateUser(user, {
						profile: {
							competences: newCompetences,
						},
					});

				setCompetences(initCompetence(newCompetences));
			}
		},
		[user, onUpdateUser, setCompetences]
	);

	const updateCompetences = useCallback(
		(newCompetence: Competence[]) => {
			const skillRemovedCompetences = newCompetence?.map((item) => {
				if (item?.skills?.length === 0) delete item.skills;
				return item;
			});

			handleUpdateUser(skillRemovedCompetences);
			setResetCompetences(false);
		},
		[handleUpdateUser, setResetCompetences]
	);

	const onSubmit = useCallback(
		handleSubmit(async (e) => {
			if (e.competence.branch && e.competence.service) {
				updateCompetences(addNewCompetence(e.competence, competences));
			} else toast.error(t("INVALID_COMPETENCE"));

			setResetCompetences(true);
		}),
		[competences, addNewCompetence, updateCompetences, setResetCompetences]
	);

	const handleRemoveSkills = useCallback(
		(branch: string, service: string, skill?: string) => {
			updateCompetences(removeCompetence(competences, branch, service, skill));
		},
		[competences, updateCompetences]
	);

	const handleSetUpModal = (item: Competence) => {
		const salaries = user.profile.employment?.salary || [];

		if (salaries.length === 0 || !salaries[0].amount) {
			toast.error(t("SALARY_DETAILS_REQUIRED"));
			return;
		}

		if (!holidayPay) {
			toast.error("Ingen kollektivavtal tilldelat");
			return;
		}

		setSupplementValueModal(true);
		setSupplementCompetence(item);
	};

	const handleUpdateSupplementValue = (value?: number) => {
		if (supplementCompetence) supplementCompetence.supplement = value;

		const skillRemovedCompetences = competences?.map((item) => {
			if (
				item.branch === supplementCompetence?.branch &&
				item.service === supplementCompetence.service
			)
				item.supplement = supplementCompetence?.supplement;

			if (item.skills?.length === 0) delete item.skills;
			return item;
		});

		handleUpdateUser(skillRemovedCompetences);
	};

	const handleModalCancel = useCallback(() => {
		setSupplementValueModal(false);
	}, [setSupplementValueModal]);

	useEffect(() => {
		if (user) setCompetences(initCompetence(user?.profile?.competences || []));
	}, [user]);

	// Flatten competences into one row per skill (or one row when skill-less),
	// then sort A-Ö by resolved label: branch → service → skill.
	const collator = new Intl.Collator("sv");
	const sortedRows = competences
		.flatMap((item) =>
			item.skills && item.skills.length > 0
				? item.skills.map((skill, index) => ({ item, skill, index }))
				: [{ item, skill: undefined as string | undefined, index: 0 }]
		)
		.map((row) => ({
			...row,
			labels: getCompetencesLabels(organisation?.competences || [], row.item, row.index),
		}))
		.sort(
			(a, b) =>
				collator.compare(a.labels.branch || "", b.labels.branch || "") ||
				collator.compare(a.labels.service || "", b.labels.service || "") ||
				collator.compare(a.labels.skills.join(", "), b.labels.skills.join(", "))
		);

	return (
		<FormCard title="COMPETENCE">
			<form onSubmit={onSubmit}>
				<CompetencesController
					control={control}
					name="competence"
					resetCompetences={resetCompetences}
					hideLabel
				/>

				<button
					type="submit"
					className="mt-4 w-full rounded-md border-0 bg-brand px-4 py-2.5 text-sm font-semibold text-brand-foreground transition-colors hover:bg-brand/90"
				>
					{t("ADD_COMPETENCE")}
				</button>
			</form>

			<div className="mt-5 border-t border-gray-200 pt-4">
				{sortedRows.length > 0 ? (
					<div>
						{sortedRows.map((row) => (
							<CompetenceRow
								key={`${row.item.branch}_${row.item.service}_${row.skill ?? row.index}`}
								index={row.index}
								item={row.item}
								skill={row.skill}
								onSetupModal={handleSetUpModal}
								onRemoveSkills={handleRemoveSkills}
							/>
						))}
					</div>
				) : (
					<p className="text-sm text-gray-500">{t("NO_COMPETENCE")}</p>
				)}
			</div>

			{supplementValueModal && (
				<SupplementValueModal
					onCancelCallBack={handleModalCancel}
					onUpdateSupplementValue={handleUpdateSupplementValue}
					value={supplementCompetence}
					holidayPay={holidayPay}
					hourlyPay={(user.profile.employment?.salary || [])[0]?.amount}
				/>
			)}
		</FormCard>
	);
};

export default CompetenceTab;
